/**
 * V3.3: Distribution Report Configuration Step
 * Allows users to select a form component to analyze response distribution
 */

import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { trpc } from '../../../../../utils/trpc'
import { useReportBuilderStore } from '../../stores/reportBuilderStore'

// Component types that can be used for distribution analysis
const DISTRIBUTION_COMPONENT_TYPES = ['rating', 'select', 'number', '9boxrating', 'checkbox']

interface FormComponent {
	id: string
	name: string
	label: string
	componentType: string
	sectionLabel?: string
}

export function StepDistribution() {
	const { currentConfig, updateConfig, nextStep, prevStep } = useReportBuilderStore()

	const [selectedComponentId, setSelectedComponentId] = useState<string>(currentConfig.distributionComponentId || '')

	// Fetch form fields for the selected forms
	const formFieldsQuery = useQuery({
		...trpc.client.reportBuilder.getFormFields.queryOptions({
			formIds: currentConfig.selectedFormIds,
		}),
		enabled: currentConfig.selectedFormIds.length > 0,
	})

	// Filter components suitable for distribution analysis
	const distributionComponents = useMemo(() => {
		if (!formFieldsQuery.data) return []

		const components: FormComponent[] = []
		for (const field of formFieldsQuery.data) {
			if (DISTRIBUTION_COMPONENT_TYPES.includes(field.componentType)) {
				components.push({
					id: field.id,
					name: field.componentType,
					label: field.label || field.componentType,
					componentType: field.componentType,
					sectionLabel: field.sectionLabel,
				})
			}
		}
		return components
	}, [formFieldsQuery.data])

	// Group components by section
	const componentsBySection = useMemo(() => {
		const groups = new Map<string, FormComponent[]>()
		for (const component of distributionComponents) {
			const section = component.sectionLabel || 'Other'
			const group = groups.get(section) || []
			group.push(component)
			groups.set(section, group)
		}
		return groups
	}, [distributionComponents])

	const handleComponentSelect = useCallback(
		(componentId: string) => {
			setSelectedComponentId(componentId)
			updateConfig({ distributionComponentId: componentId })
		},
		[updateConfig],
	)

	const selectedComponent = distributionComponents.find((c) => c.id === selectedComponentId)

	const canProceed = !!selectedComponentId

	return (
		<div className='space-y-6'>
			<div>
				<h2 className='text-xl font-semibold text-neutral-900 mb-2'>Distribution Analysis</h2>
				<p className='text-sm text-neutral-600'>
					Select a form component to analyze the distribution of responses. This report will show how responses are
					distributed across different values.
				</p>
			</div>

			{/* Form selection reminder */}
			{currentConfig.selectedFormIds.length === 0 && (
				<div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
					<p className='text-sm text-amber-700'>Please go back and select at least one form to analyze.</p>
				</div>
			)}

			{/* Loading state */}
			{formFieldsQuery.isLoading && (
				<div className='flex items-center justify-center py-8'>
					<svg className='w-6 h-6 animate-spin text-blue-500' viewBox='0 0 24 24' aria-hidden='true'>
						<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
						<path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
					</svg>
					<span className='ml-2 text-neutral-600'>Loading form components...</span>
				</div>
			)}

			{/* Component selector */}
			{!formFieldsQuery.isLoading && distributionComponents.length > 0 && (
				<div className='space-y-4'>
					<span className='block text-sm font-medium text-neutral-700'>Select Component for Distribution Analysis</span>

					<div className='border border-neutral-200 rounded-lg divide-y divide-neutral-200 max-h-96 overflow-y-auto'>
						{Array.from(componentsBySection.entries()).map(([section, components]) => (
							<div key={section} className='p-3'>
								<h4 className='text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2'>{section}</h4>
								<div className='space-y-2'>
									{components.map((component) => (
										<label
											key={component.id}
											className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
												selectedComponentId === component.id
													? 'bg-blue-50 border-2 border-blue-500'
													: 'bg-neutral-50 border border-neutral-200 hover:border-blue-300'
											}`}>
											<input
												type='radio'
												name='distributionComponent'
												value={component.id}
												checked={selectedComponentId === component.id}
												onChange={() => handleComponentSelect(component.id)}
												className='h-4 w-4 text-blue-600 border-neutral-300 focus:ring-blue-500'
											/>
											<div className='ml-3 flex-1'>
												<span className='block text-sm font-medium text-neutral-900'>{component.label}</span>
												<span className='block text-xs text-neutral-500'>Type: {component.componentType}</span>
											</div>
										</label>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* No suitable components message */}
			{!formFieldsQuery.isLoading &&
				distributionComponents.length === 0 &&
				currentConfig.selectedFormIds.length > 0 && (
					<div className='p-4 bg-neutral-50 border border-neutral-200 rounded-lg'>
						<p className='text-sm text-neutral-600'>
							No suitable components found for distribution analysis. Distribution reports work best with rating,
							select, number, or checkbox components.
						</p>
					</div>
				)}

			{/* Selected component preview */}
			{selectedComponent && (
				<div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
					<h4 className='text-sm font-medium text-blue-900 mb-2'>Selected Component</h4>
					<p className='text-sm text-blue-700'>
						<strong>{selectedComponent.label}</strong> ({selectedComponent.componentType})
					</p>
					<p className='text-xs text-blue-600 mt-1'>
						The report will show how responses are distributed for this component across all selected forms and cycles.
					</p>
				</div>
			)}

			{/* Navigation */}
			<div className='flex justify-between pt-4 border-t border-neutral-200'>
				<button
					type='button'
					onClick={prevStep}
					className='px-6 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors font-medium'>
					Back
				</button>
				<button
					type='button'
					onClick={nextStep}
					disabled={!canProceed}
					className='px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'>
					Next: Apply Filters
				</button>
			</div>
		</div>
	)
}
